import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  getDashboardTouchZone,
  MOBILE_SCROLL_TRACE_EVENT,
  type MobileScrollTraceEntry,
} from "@/lib/mobileScrollTrace";

type ScrollOwner = {
  name: string;
  getScrollTop: () => number;
};

type ActiveGesture = {
  target: string;
  owner: ScrollOwner;
  scrollTopBefore: number;
  zone: string | null;
  moveLogged: boolean;
};

const MAX_TRACE_ENTRIES = 14;

function isTraceRoute(location: string): boolean {
  return /^\/loan-application\/[^/]+\/collateral\/[^/]+\/edit(?:\?|$)/.test(location)
    || /^\/loan-applications(?:\?|$)/.test(location)
    || /^\/my-applications(?:\?|$)/.test(location)
    || /^\/dashboard(?:\?|$)/.test(location);
}

function describeTarget(target: EventTarget | null): string {
  if (!(target instanceof Element)) return "target=unknown";

  const markedTarget = target.closest<HTMLElement>("[data-mobile-trace-target]")?.dataset.mobileTraceTarget;
  if (markedTarget) return `target=${markedTarget}`;

  if (target.closest("button")) return "target=button";
  if (target.closest("input, textarea, select")) return "target=form-control";
  if (target.closest("a")) return "target=link";

  const role = target.closest<HTMLElement>("[role]")?.getAttribute("role");
  return role ? `target=role:${role}` : `target=${target.tagName.toLowerCase()}`;
}

function getScrollOwner(target: EventTarget | null): ScrollOwner {
  if (target instanceof Element) {
    const markedOwner = target.closest<HTMLElement>("[data-mobile-scroll-owner]");
    if (markedOwner) {
      return {
        name: markedOwner.dataset.mobileScrollOwner || "marked-scroll-owner",
        getScrollTop: () => markedOwner.scrollTop,
      };
    }
  }

  return {
    name: "document",
    getScrollTop: () => document.scrollingElement?.scrollTop ?? window.scrollY ?? 0,
  };
}

export function MobileScrollTracePanel() {
  const [location] = useLocation();
  const [entries, setEntries] = useState<string[]>([]);
  const activeGestureRef = useRef<ActiveGesture | null>(null);
  const traceRequested = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("debug") === "1";
  const { data: user } = trpc.auth.me.useQuery(undefined, { enabled: traceRequested });
  const traceEnabled = traceRequested && user?.role === "admin" && isTraceRoute(location);

  const append = useCallback((entry: string) => {
    const timestamp = new Date().toISOString().slice(11, 23);
    setEntries(current => [...current.slice(-(MAX_TRACE_ENTRIES - 1)), `${timestamp} ${entry}`]);
  }, []);

  useEffect(() => {
    setEntries([]);
    activeGestureRef.current = null;
  }, [location, traceEnabled]);

  useEffect(() => {
    if (!traceEnabled) return;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const owner = getScrollOwner(event.target);
      const target = describeTarget(event.target);
      const scrollTopBefore = owner.getScrollTop();
      const isDashboard = location.split("?")[0] === "/dashboard";
      const fab = isDashboard ? document.querySelector<HTMLElement>("[data-testid='floating-action-button']") : null;
      const zone = isDashboard
        ? getDashboardTouchZone(event.touches[0], fab?.getBoundingClientRect() ?? null, window.innerWidth)
        : null;
      activeGestureRef.current = { target, owner, scrollTopBefore, zone, moveLogged: false };
      append(`touch-start | ${target}${zone ? ` | zone=${zone}` : ""} | owner=${owner.name} | scrollTop=${Math.round(scrollTopBefore)}`);
    };

    const handleTouchMove = (event: TouchEvent) => {
      const gesture = activeGestureRef.current;
      if (!gesture || gesture.moveLogged) return;
      gesture.moveLogged = true;
      window.setTimeout(() => {
        append(`touch-move | ${gesture.target}${gesture.zone ? ` | zone=${gesture.zone}` : ""} | owner=${gesture.owner.name} | prevented=${event.defaultPrevented}`);
      }, 0);
    };

    const handleTouchEnd = () => {
      const gesture = activeGestureRef.current;
      if (!gesture) return;
      const scrollTopAfter = gesture.owner.getScrollTop();
      append(
        `touch-end | ${gesture.target}${gesture.zone ? ` | zone=${gesture.zone}` : ""} | owner=${gesture.owner.name} | scrollTop=${Math.round(gesture.scrollTopBefore)}→${Math.round(scrollTopAfter)} | delta=${Math.round(scrollTopAfter - gesture.scrollTopBefore)}`,
      );
      activeGestureRef.current = null;
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      append(`pointer-down | ${describeTarget(event.target)} | pointer=touch`);
    };

    const handleTrace = (event: Event) => {
      const traceEvent = event as CustomEvent<MobileScrollTraceEntry>;
      append(`${traceEvent.detail.phase} | ${traceEvent.detail.detail}`);
    };

    document.addEventListener("touchstart", handleTouchStart, true);
    document.addEventListener("touchmove", handleTouchMove, false);
    document.addEventListener("touchend", handleTouchEnd, true);
    document.addEventListener("touchcancel", handleTouchEnd, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener(MOBILE_SCROLL_TRACE_EVENT, handleTrace as EventListener);

    append(`trace-ready | route=${location.split("?")[0]} | privacy=no-record-data,no-coordinates`);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart, true);
      document.removeEventListener("touchmove", handleTouchMove, false);
      document.removeEventListener("touchend", handleTouchEnd, true);
      document.removeEventListener("touchcancel", handleTouchEnd, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener(MOBILE_SCROLL_TRACE_EVENT, handleTrace as EventListener);
    };
  }, [append, location, traceEnabled]);

  if (!traceEnabled) return null;

  return (
    <section
      data-testid="mobile-scroll-trace"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 top-[calc(env(safe-area-inset-top)+4rem)] z-[90] max-h-44 overflow-y-auto rounded-xl border border-amber-300 bg-amber-50/95 px-3 py-2 text-[10px] leading-4 text-amber-950 shadow-lg lg:hidden"
    >
      <p className="font-semibold">ກວດສອບ Scroll/FAB v320 — ບໍ່ເກັບຂໍ້ມູນລູກຄ້າ, ຂໍ້ຄວາມ ຫຼື ພິກັດ</p>
      <div className="mt-1 space-y-0.5 font-mono">
        {entries.length === 0 ? <p>ລໍຖ້າການ touch/scroll…</p> : entries.map((entry, index) => <p key={`${entry}-${index}`}>{entry}</p>)}
      </div>
    </section>
  );
}
